import { FormControl } from '@mui/material';
import { useOutletContext } from "react-router-dom";

import Page from "~/components/Page";
import Filter from "~/components/Filter";
import AdminNotificationsTable from "~/tables/AdminNotificationsTable";
import { useTrans } from "~/hooks/useTrans";
import { apiGetProject, apiGetProjectNotifications, apiGetProjectNotificationBody } from "~/providers/api";
import TableControls from '~/components/TableControls';
import { IAdminProject, IProjectNotification } from "~/providers/admin-interfaces";
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import { IOutlet } from "~/interfaces";
import MyAlert from "~/mui/MyAlert";

import "./style.less";

type TFilter = "email" | "none";

interface IState {
	loading: boolean;
	isDirty: boolean;
	project: IAdminProject;
	notifications: Array<IProjectNotification>;
	notificationsOrig: Array<IProjectNotification>;
	errorMsg: string;
	notificationBody: string;
}

const INIT_STATE: IState = {
	loading: true,
	isDirty: false,
	project: null,
	notifications: [],
	notificationsOrig: [],
	errorMsg: "",
	notificationBody: "",
};

export default function ProjectNotificationsPage() {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, setState, updateState } = myUseState<IState>(INIT_STATE);
	const { params }: IOutlet = useOutletContext();

	function onFilter(filterType: TFilter, filterValue: string) {
		setState(prev => {
			const filtered = prev.notificationsOrig.filter(item => {
				switch (filterType) {
					case "email":
						return item.emailRecipients.indexOf(filterValue) !== -1;

					default:
				}

				return true;
			});

			return {
				...prev,
				notifications: filtered,
			};
		});
	}

	async function onShowBody(id: IProjectNotification["id"]) {
		const bodyData = await apiGetProjectNotificationBody(params.projectId, id, lang);

		if (bodyData.noErrors) {
			updateState({
				notificationBody: bodyData.data.emailBody,
			});
		} else {
			updateState({
				errorMsg: bodyData.errors.join(", "),
			});
		}
	}

	async function loadData() {
		updateState(INIT_STATE);

		const [projectData, notificationsData] = await Promise.all([
			apiGetProject("admin", params.projectId, lang),
			apiGetProjectNotifications(params.projectId, lang),
		]);

		updateState({
			loading: false,
			// castecna data
			...projectData.noErrors
				? {
					project: projectData.data,
				}
				: {},
			...notificationsData.noErrors && notificationsData.data.length
				? {
					notifications: notificationsData.data.slice(),
					notificationsOrig: notificationsData.data,
				}
				: {},
			errorMsg: [
				...projectData.errors,
				...notificationsData.errors,
			].join(", "),
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	return <Page checkLogged={true} title={trans("menu.notificationsHistory")} breadcrumbs={[trans("administration"), trans("projects")]} checkInternalRole="salesman" onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.admin.projects">
		<TableControls className="projectNotificationsPage__tableControls">
			<div className="projectNotificationsPage__filterCont">
				<Filter<TFilter> type="none" disabledTypes={["none"]} onFilter={onFilter}
					selectTitle={trans("adminProjectsPage.filter.title")} inputTitle={trans("adminProjectsPage.filter.title")}
					selectItems={[
						{ value: "email", text: trans("adminProjectNotifications.filter.types.email") },
						{ value: "none", text: trans("adminProjectsPage.filter.types.none") },
					]} />
			</div>
			<FormControl className="projectNotificationsPage__addForm" size="small">
			</FormControl>
		</TableControls>
		{ !state.loading && <AdminNotificationsTable items={state.notifications} loading={state.loading} onShowBody={onShowBody} /> }
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
		{ state.notificationBody && <MyAlert title={trans("adminProjectNotifications.showBody.title")} maxWidth={false} onClose={() => updateState({ notificationBody: "" })} cancelCaption={trans("close")}>
			<div dangerouslySetInnerHTML={{ __html: state.notificationBody }} />
		</MyAlert> }
	</Page>;
}
